import { Button } from '@fluentui/react-button';
import { Spinner, makeStyles } from '@fluentui/react-components';
import { ErrorCircleRegular } from '@fluentui/react-icons';
import { Tooltip } from '@fluentui/react-tooltip';
import React, { useCallback, useState } from 'react';
import { COPY } from '../../../assets/strings';
import { useChat } from '../../../libs/hooks';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { FeatureKeys } from '../../../redux/features/app/AppState';
import { Breakpoints } from '../../../styles';
import { ArrowDownload16, Edit, Share20 } from '../../shared/BundledIcons';
import { InvitationCreateDialog } from '../invitation-dialog/InvitationCreateDialog';
import { DeleteChatDialog } from './dialogs/DeleteChatDialog';

const useClasses = makeStyles({
    root: {
        display: 'contents',
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    loading: {
        paddingLeft: '8px',
        paddingRight: '8px',
    }
});

interface IListItemActionsProps {
    chatId: string;
    onEditTitleClick: () => void;
}

export const ListItemActions: React.FC<IListItemActionsProps> = ({ chatId, onEditTitleClick }) => {
    const classes = useClasses();
    const { features } = useAppSelector((state: RootState) => state.app);
    const { conversations } = useAppSelector((state: RootState) => state.conversations);
    const [isLoading, setIsLoading] = useState(false);

    const chat = useChat();
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

    const onDownloadBotClick = useCallback(() => {
        setIsLoading(true);
        void chat.downloadPdf(chatId).then((content) => {
            if (content) {
                const blob = base64ToBlob(content.base64Pdf);
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = url;
                link.download = `${content.chatTitle}.pdf`;
                link.click();
                URL.revokeObjectURL(url);
            } else {
                console.log('Error downloading chat');
            }
            setIsLoading(false);
        });
    }, [chat, chatId]);

    // Function to convert base64 to Blob
    const base64ToBlob = (base64: string, type = 'application/pdf') => {
        const binStr = atob(base64);
        const len = binStr.length;
        const arr = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
            arr[i] = binStr.charCodeAt(i);
        }

        return new Blob([arr], { type });
    };

    return (
        <div className={classes.root}>
            {conversations[chatId].disabled ? (
                <Tooltip content={COPY.CHAT_DELETED_MESSAGE()} relationship="label">
                    <Button
                        icon={<ErrorCircleRegular />}
                        appearance="transparent"
                        aria-label="Alert: Chat has been deleted by another user."
                    />
                </Tooltip>
            ) : (
                <>
                    <Tooltip content={'Edit chat name'} relationship="label">
                        <Button
                            icon={<Edit />}
                            appearance="transparent"
                            aria-label="Edit chat name"
                            onClick={onEditTitleClick}
                            data-testid="editChatTitleButtonSimplified"
                        />
                    </Tooltip>
                    <Tooltip content={'Download chat session'} relationship="label">
                        {isLoading ? (
                            <Spinner size="extra-tiny" className={classes.loading}/>
                        ) : (
                            <Button
                                icon={<ArrowDownload16 />}
                                appearance="transparent"
                                aria-label="Download chat session"
                                onClick={onDownloadBotClick}
                            />
                        )}
                    </Tooltip>
                    <Tooltip content={'Share your chat'} relationship="label">
                        <Button
                            disabled={!features[FeatureKeys.MultiUserChat].enabled}
                            icon={<Share20 />}
                            appearance="transparent"
                            aria-label="Share your chat"
                            onClick={() => {
                                setIsShareDialogOpen(true);
                            }}
                        />
                    </Tooltip>
                    <DeleteChatDialog chatId={chatId} />
                    <InvitationCreateDialog
                        open={isShareDialogOpen}
                        onCloseDialog={() => {
                            setIsShareDialogOpen(false);
                        }}
                        chatId={chatId}
                    />
                </>
            )}
        </div>
    );
};
